import { graphql, Slice, navigate } from 'gatsby';
import React, { useState, useEffect } from 'react';
import { includes, find, isNumber } from 'lodash';

import Default from '~/templates/Default';

import Seo from '~/layout/SEO';

import ProductHeader from '~/components/products/ProductHeader';
import AddOnCarousel from '~/components/products/AddOnCarousel';

import { useLocationPickerContext } from '~/context/LocationPickerContext';
import { getProductPrice } from '~/utilities/products';

const getProduct = (products, selectedLocation) => {
    if (products.length === 1) {
        return products[0].node;
    }

    if (!selectedLocation) {
        return products[0].node;
    }

    return find(products, ({ node }) =>
        includes(node.locations, selectedLocation.ampID)
    ).node;
};

const Product = ({ data }) => {
    const {
        allWpProduct: { edges: products },
        wp: {
            siteSettings: {
                siteSettings: { addOnsTitle, addOnsDescription }
            }
        }
    } = data;

    const { selectedLocation } = useLocationPickerContext();

    const [productLocation, setProductLocation] = useState(selectedLocation);
    const [product, setProduct] = useState(
        getProduct(products, productLocation)
    );

    const {
        locations,
        productInformation: { addOns }
    } = product;

    useEffect(() => {
        if (products.length === 1) {
            if (
                locations.length &&
                productLocation &&
                !includes(locations, productLocation.ampID)
            ) {
                navigate('/');
            }
        } else {
            if (!productLocation) {
                setProduct(products[0].node);
            } else {
                setProduct(getProduct(products, productLocation));
            }
        }
    }, [productLocation, products, locations]);

    useEffect(() => {
        if (selectedLocation) {
            setProductLocation(selectedLocation);
        }
    }, [selectedLocation]);

    return (
        <Default>
            <ProductHeader
                {...{ product, productLocation, setProductLocation }}
            />
            {addOns && addOns.length && (
                <AddOnCarousel
                    title={addOnsTitle}
                    description={addOnsDescription}
                    {...{ addOns }}
                />
            )}
            <Slice alias="product-page-blocks" />
        </Default>
    );
};

export default Product;

export const Head = ({
    location,
    data: {
        allWpProduct: { edges: products }
    }
}) => {
    const { selectedLocation } = useLocationPickerContext();
    const product = getProduct(products, selectedLocation);
    const productPrice = getProductPrice(
        product,
        product.productTypes.nodes[0].slug,
        selectedLocation?.priceGroup ?? null,
        false
    );
    const price =
        productPrice && isNumber(productPrice)
            ? Number((productPrice / 100).toFixed(2))
            : 0.0;

    return (
        <Seo
            {...{ location, price }}
            seoFragment={product.seo}
            sku={product.slug}
        />
    );
};

export const query = graphql`
    query Product($ids: [String!]!) {
        allWpProduct(filter: { id: { in: $ids } }) {
            edges {
                node {
                    ampId
                    seo {
                        ...SeoFragment
                    }
                    databaseId
                    title
                    slug
                    displayName
                    locations
                    featuredImage {
                        node {
                            altText
                            sourceUrl
                            localFile {
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                        }
                    }
                    stripePrices {
                        id
                        priceGroup
                        label
                        stripeId
                        unitAmount
                        interval
                        interval_count
                        ordinal
                    }
                    productInformation {
                        shortDescription
                        productDescription
                        price
                        bogo {
                            prepaidPromotion {
                                ... on WpPrepaidPromotion {
                                    title
                                    subTitle
                                    ampId
                                    effectiveFrom
                                    effectiveTo
                                    ordinal
                                    percentOff
                                    price
                                    promotionQuantity
                                    purchaseLimit
                                    purchaseQuantity
                                    savingsLabel
                                    washTypeIds
                                }
                            }
                        }
                        addOns {
                            ... on WpAddOn {
                                id
                                databaseId
                                title
                                ampId
                                addOnInformation {
                                    shortDescription
                                    price
                                }
                            }
                        }
                    }
                    productImageSettings {
                        imagePosition
                    }
                    giftCardValues
                    giftCardQuantity
                    productCategories {
                        nodes {
                            name
                            id
                        }
                    }
                    productTypes {
                        nodes {
                            slug
                        }
                    }
                    cleaningCategories {
                        nodes {
                            name
                            id
                        }
                    }
                }
            }
        }
        wp {
            siteSettings {
                siteSettings {
                    addOnsTitle
                    addOnsDescription
                }
            }
        }
    }
`;
